import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import StripePaymentForm from "@/components/StripePaymentForm";
import SepaPaymentForm from "@/components/SepaPaymentForm";
import { useToast } from "@/hooks/use-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const eur = (v) =>
  (v ?? 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#EAE8E4] font-sans antialiased flex flex-col">
    <header className="sticky top-0 z-50 w-full border-b border-slate-200/60 bg-[#EAE8E4]/90 backdrop-blur-md">
      <div className="max-w-xl mx-auto flex h-16 items-center px-4 sm:px-6">
        <Link to="/" className="flex flex-col items-start">
          <img src="/assets/logo.png" alt="Hafani" className="h-9 w-auto object-contain" />
          <span className="text-[9px] text-emerald-700 font-medium mt-0.5 tracking-wide">
            Hafani, une solution pensée pour notre fin
          </span>
        </Link>
      </div>
    </header>
    <main className="flex-1 max-w-xl mx-auto w-full px-4 sm:px-6 py-10 lg:py-14">{children}</main>
    <footer className="py-6 text-center border-t border-slate-200/60">
      <p className="text-xs text-slate-400 font-light">
        &copy; {new Date().getFullYear()} Hafani Management and Solutions
      </p>
    </footer>
  </div>
);

const Card = ({ children }) => (
  <div className="bg-white rounded-3xl p-7 sm:p-9 shadow-sm border border-slate-100 space-y-6">{children}</div>
);

const Notice = ({ icon: Icon, tone, title, children }) => (
  <Card>
    <div className="text-center space-y-3 py-4">
      <Icon className={`h-9 w-9 mx-auto ${tone}`} />
      <p className="font-semibold text-slate-900">{title}</p>
      {children && <p className="text-sm text-slate-500">{children}</p>}
    </div>
  </Card>
);

export const LinkPaymentPage = () => {
  const { token } = useParams();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [loadError, setLoadError] = useState(false);

  // choose | pay_full | pay_deposit | mandate | done
  const [phase, setPhase] = useState("choose");
  const [busy, setBusy] = useState(false);

  const [cardSecret, setCardSecret] = useState(null);
  const [cardAmount, setCardAmount] = useState(null);

  const [sepaSecret, setSepaSecret] = useState(null);
  const [result, setResult] = useState(null);

  const fetchInfo = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/payments/stripe/link/${token}`);
      setInfo(res.data);
      if (res.data.state === "active" && res.data.deposit_paid) {
        setPhase("mandate");
      }
    } catch (err) {
      if (err?.response?.status === 404) setInfo({ state: "notfound" });
      else setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchInfo(); }, [fetchInfo]);

  // Charge le SetupIntent SEPA dès qu'on entre dans la phase mandat.
  useEffect(() => {
    if (phase !== "mandate" || sepaSecret) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post(`${API}/payments/stripe/link/${token}/sepa-setup`);
        if (!cancelled) setSepaSecret(res.data.client_secret);
      } catch (err) {
        if (!cancelled) {
          toast({
            title: "Erreur",
            description: err?.response?.data?.detail || "Impossible d'initialiser le prélèvement SEPA.",
            variant: "destructive",
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [phase, sepaSecret, token, toast]);

  const chooseFormula = async (formula) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/payments/stripe/link/${token}/intent`, { formula });
      if (res.data.deposit_already_paid) {
        setPhase("mandate");
        return;
      }
      setCardSecret(res.data.client_secret);
      setCardAmount(res.data.amount);
      setPhase(formula === "full" ? "pay_full" : "pay_deposit");
    } catch (err) {
      toast({
        title: "Erreur",
        description: err?.response?.data?.detail || "Impossible d'initialiser le paiement.",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const handleCardSuccess = () => {
    if (phase === "pay_full") {
      setResult({ mode: "full" });
      setPhase("done");
    } else {
      // acompte réglé → on enchaîne sur le mandat SEPA
      setCardSecret(null);
      setPhase("mandate");
    }
  };

  const confirmMandate = (payload) =>
    axios
      .post(`${API}/payments/stripe/link/${token}/sepa-subscription`, payload)
      .then((r) => r.data);

  if (loading) {
    return (
      <Shell>
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
        </div>
      </Shell>
    );
  }

  if (loadError) {
    return (
      <Shell>
        <Notice icon={AlertTriangle} tone="text-amber-500" title="Lien indisponible">
          Une erreur est survenue. Réessayez dans un instant.
        </Notice>
      </Shell>
    );
  }

  if (info?.state === "notfound") {
    return (
      <Shell>
        <Notice icon={AlertTriangle} tone="text-amber-500" title="Lien de paiement introuvable">
          Ce lien n'est pas valide. Rapprochez-vous de votre association.
        </Notice>
      </Shell>
    );
  }

  if (info?.state === "expired") {
    return (
      <Shell>
        <Notice icon={AlertTriangle} tone="text-amber-500" title="Ce lien de paiement a expiré">
          Demandez un nouveau lien à votre association.
        </Notice>
      </Shell>
    );
  }

  if (info?.state === "settled") {
    return (
      <Shell>
        <Notice icon={CheckCircle2} tone="text-emerald-600" title="Votre cotisation est déjà réglée">
          Merci pour votre contribution à la solidarité {info.association_name}.
        </Notice>
      </Shell>
    );
  }

  if (phase === "done") {
    return (
      <Shell>
        <Notice icon={CheckCircle2} tone="text-emerald-600" title={
          result?.mode === "full"
            ? "Paiement reçu. Merci."
            : result?.deposit_waived
              ? "Prélèvement mis en place. Merci."
              : "Acompte enregistré. Merci."
        }>
          {result?.mode === "installments" && (
            <>
              Vous serez prélevé de <strong>{eur(result.monthly_amount)} €</strong> par mois
              {result.next_payment_date ? <> à partir du <strong>{result.next_payment_date}</strong></> : null}, pendant{" "}
              {result.installments ?? 12} mois. Le prélèvement s'arrête automatiquement après la dernière mensualité.
            </>
          )}
        </Notice>
      </Shell>
    );
  }

  const remaining = info?.remaining_amount ?? 0;
  const depositFormula = info?.formulas?.deposit_installments;
  const depositWaived = info?.deposit_waived ?? depositFormula?.deposit_waived ?? false;

  return (
    <Shell>
      <div className="text-center mb-7">
        <h1 className="font-serif text-4xl sm:text-5xl text-slate-900 leading-tight">
          Régler ma <em className="not-italic text-emerald-700">cotisation.</em>
        </h1>
        <p className="text-slate-400 text-sm font-light mt-3">
          {info?.first_name ? `${info.first_name}, ` : ""}communauté {info?.association_name}
        </p>
      </div>

      <Card>
        {phase === "mandate" ? (
          <>
            <div className="rounded-2xl bg-emerald-50 border border-emerald-100 px-4 py-3 text-sm text-emerald-800">
              Acompte réglé ✓ — il reste à autoriser le prélèvement du solde.
            </div>
            {sepaSecret ? (
              <Elements stripe={stripePromise} options={{ clientSecret: sepaSecret }}>
                <SepaPaymentForm
                  clientSecret={sepaSecret}
                  monthlyAmount={
                    info?.installment?.monthly_amount ?? depositFormula?.monthly_amount ?? remaining / 12
                  }
                  remainingAmount={remaining}
                  member={{ first_name: info?.first_name }}
                  associationName={info?.association_name}
                  onConfirmMandate={confirmMandate}
                  onSuccess={(data) => { setResult({ mode: "installments", deposit_waived: depositWaived, ...data }); setPhase("done"); }}
                  onError={(e) => toast({ title: "Erreur", description: e.message, variant: "destructive" })}
                />
              </Elements>
            ) : (
              <div className="flex justify-center py-6">
                <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
              </div>
            )}
          </>
        ) : phase === "pay_full" || phase === "pay_deposit" ? (
          <>
            <div className="flex justify-between items-baseline">
              <span className="text-sm font-semibold text-slate-900">
                {phase === "pay_deposit" ? "Acompte à régler" : "Montant à régler"}
              </span>
              <span className="font-serif text-3xl tracking-tight text-emerald-700">{eur(cardAmount)} €</span>
            </div>
            {cardSecret && (
              <Elements key={cardSecret} stripe={stripePromise} options={{ clientSecret: cardSecret }}>
                <StripePaymentForm
                  clientSecret={cardSecret}
                  amount={cardAmount || remaining}
                  onSuccess={handleCardSuccess}
                  onError={(e) => toast({ title: "Échec du paiement", description: e.message, variant: "destructive" })}
                />
              </Elements>
            )}
            <button
              type="button"
              onClick={() => { setCardSecret(null); setPhase("choose"); }}
              className="w-full text-xs text-slate-400 hover:text-slate-700 transition-colors"
            >
              ← Changer de formule
            </button>
          </>
        ) : (
          <>
            <div className="border-b border-slate-100 pb-4 flex justify-between items-baseline">
              <span className="text-sm font-semibold text-slate-900">Reste à régler</span>
              <span className="font-serif text-4xl tracking-tight text-emerald-700">{eur(remaining)} €</span>
            </div>

            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Choisissez votre formule</p>

            <button
              type="button"
              disabled={busy}
              onClick={() => chooseFormula("full")}
              className="w-full text-left rounded-2xl border border-slate-200 hover:border-emerald-400 p-5 transition-all disabled:opacity-50"
            >
              <p className="font-semibold text-slate-900">Payer l'intégralité</p>
              <p className="text-sm text-slate-500 mt-0.5">{eur(remaining)} € en une fois, par carte bancaire.</p>
            </button>

            {depositFormula ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => chooseFormula("deposit_installments")}
                className="w-full text-left rounded-2xl border border-slate-200 hover:border-emerald-400 p-5 transition-all disabled:opacity-50"
              >
                <p className="font-semibold text-slate-900">
                  {depositWaived ? "Prélèvement mensuel" : "Acompte puis mensualités"}
                </p>
                <p className="text-sm text-slate-500 mt-0.5">
                  {depositWaived ? (
                    <>
                      Vos versements couvrent déjà l'acompte — mise en place immédiate,{" "}
                      <strong>{depositFormula.installments}× {eur(depositFormula.monthly_amount)} €</strong> par prélèvement SEPA mensuel.
                    </>
                  ) : (
                    <>
                      <strong>{eur(depositFormula.deposit_amount)} €</strong> aujourd'hui par carte, puis{" "}
                      <strong>{depositFormula.installments}× {eur(depositFormula.monthly_amount)} €</strong> par prélèvement SEPA mensuel.
                    </>
                  )}
                </p>
              </button>
            ) : null}

            {busy && (
              <div className="flex justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />
              </div>
            )}
          </>
        )}
      </Card>
    </Shell>
  );
};

export default LinkPaymentPage;
